import classNames from "classnames"

import { Color } from "../../../../constants/V2/color"
import PillButton from "../../../elements/V2/Buttons/PillButton"
import ClickableCardLink from "../../../elements/V2/ClickableCardLink"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import Typography from "../../../elements/V2/Typography"
import LayoutItem from "../../Layouts/LayoutItem"

import Badge from "@components/elements/V2/Badge"
import YocoLogoIcon from "@components/elements/V2/Icons/YocoLogoIcon"
import Video from "@components/experiments/Video"
import {
  getAccentColor,
  getBackgroundColorClass,
  getFillColorClass,
  getGradientFromColorClass,
  getGradientToColorClass,
} from "@utils/V2/color"
import React from "react"

interface TextItem {
  text: string
  hideIcon?: boolean
}

type Props = {
  cardBackgroundColor: Color
  secondaryBackgroundColor?: Color
  header: string
  superscript?: string
  description?: string
  descriptionList?: TextItem[]
  verticalLabel?: string
  badgeText?: string
  badgeColor: Color
  reverseOrder?: boolean
  size?: "small" | "large"
  buttonText?: string
  buttonLink?: string | YocoCom.YocoLink | undefined
  buttonBorderColor: Color
  buttonStyle?: "solid" | "bordered"
  secondaryCTAText?: string
  secondaryCTALink?: string | YocoCom.YocoLink
  secondaryCTAStyle?: "solid" | "bordered"
  imageUrl?: string
  imageAlt?: string
  showLogo?: boolean
  headerLabel?: string
  mobileGridOrder?: number
  mobileImageUrl?: string
  disclaimer?: string
  descriptionHeader?: string
  videoUrl?: string
  trackingEvent?: string
  trackingEventKey?: string
  trackingEventValue?: string
}

const ContentBanner = ({
  cardBackgroundColor,
  secondaryBackgroundColor,
  header,
  superscript,
  description,
  descriptionList,
  verticalLabel,
  badgeText,
  badgeColor,
  reverseOrder = false,
  size = "small",
  buttonText,
  buttonLink,
  buttonBorderColor,
  buttonStyle = "bordered",
  secondaryCTAText,
  secondaryCTALink,
  secondaryCTAStyle = "bordered",
  imageUrl,
  imageAlt,
  mobileImageUrl,
  showLogo = false,
  headerLabel,
  mobileGridOrder,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  disclaimer,
  descriptionHeader,
  videoUrl,
  ...props
}: Props) => {
  const primaryButtonStyle = buttonStyle || "bordered"
  const hasPrimaryCTA = !!(buttonText && buttonLink)
  const hasSecondaryCTA = !!(secondaryCTAText && secondaryCTALink)
  const isSingleCTA = hasPrimaryCTA && !hasSecondaryCTA
  const accentColor = getAccentColor(cardBackgroundColor)

  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as="div"
      animateOnScroll
      className={classNames(
        "rounded-lg relative col-span-4 grid grid-cols-1 overflow-hidden sm:col-span-4 sm:grid-cols-2 md:col-span-6 lg:col-span-12",
        isSingleCTA && "group cursor-pointer",
        !secondaryBackgroundColor
          ? getBackgroundColorClass(cardBackgroundColor)
          : [
              "bg-gradient-to-br",
              getGradientFromColorClass(cardBackgroundColor),
              getGradientToColorClass(secondaryBackgroundColor),
            ]
      )}
      {...props}
    >
      <div
        className={classNames(
          "flex flex-col py-24 pl-20 pr-20 sm:py-32 md:py-48 md:pl-60",
          (showLogo || headerLabel) && size === "large"
            ? "sm:gap-32"
            : "sm:gap-16",
          size === "large" ? "justify-center gap-32" : "gap-20 md:gap-32"
        )}
      >
        {badgeText && (
          <Badge
            text={badgeText}
            textColor={getAccentColor(badgeColor)}
            accentColor={badgeColor}
            uppercaseText={true}
            style="solid"
          />
        )}
        {(showLogo || headerLabel) && (
          <div className="flex items-center justify-start gap-12">
            {showLogo && (
              <YocoLogoIcon
                height="32"
                className={classNames(
                  "w-auto items-start",
                  getFillColorClass(Color.Blue)
                )}
              />
            )}
            {headerLabel && (
              <Typography
                text={headerLabel}
                size="body-lg"
                font="grotesk"
                weight="medium"
                color={accentColor}
                className="mt-[2px]"
              />
            )}
          </div>
        )}
        <div className="flex flex-col gap-4 pb-24">
          {superscript && (
            <Typography
              text={superscript}
              font="grotesk"
              weight="book"
              size="subscript-lg"
              color={accentColor}
              className="uppercase"
            />
          )}
          <Typography
            text={header}
            font="grotesk"
            weight={size === "large" ? "medium" : "book"}
            size={size === "large" ? "h5" : "lead-lg"}
            color={accentColor}
          />
          {disclaimer && (
            <Typography
              text={disclaimer}
              font="grotesk"
              weight="book"
              size="body-md"
              color={accentColor}
            />
          )}
          {descriptionHeader && (
            <Typography
              text={descriptionHeader}
              font="grotesk"
              weight="medium"
              size="lead-md"
              color={accentColor}
              className="mt-[20px]"
            />
          )}
          {description && (
            <Typography
              text={description}
              font="grotesk"
              weight="book"
              size="body-md"
              color={accentColor}
            />
          )}
          {descriptionList?.map((descriptionItem, index) => (
            <Typography
              key={`${header}-item-${index + 1}`}
              text={`- ${descriptionItem.text}`}
              font="grotesk"
              weight="book"
              size="body-md"
              color={accentColor}
            />
          ))}
        </div>

        <div className="flex flex-col gap-32 sm:flex-row">
          {buttonText && buttonLink && (
            <PillButton
              size="large"
              style={primaryButtonStyle}
              text={buttonText}
              color={buttonBorderColor}
              linkUrl={isSingleCTA ? undefined : buttonLink}
              decorative={isSingleCTA}
              overrideTextColor={
                primaryButtonStyle === "solid"
                  ? getAccentColor(buttonBorderColor)
                  : accentColor
              }
              trackingEvent={trackingEvent}
              trackingEventKey={trackingEventKey}
              trackingEventValue={trackingEventValue}
              className="shrink-0"
            />
          )}

          {secondaryCTAText && secondaryCTALink && (
            <PillButton
              size="large"
              style={secondaryCTAStyle}
              text={secondaryCTAText}
              color={buttonBorderColor}
              linkUrl={secondaryCTALink}
              overrideTextColor={
                secondaryCTAStyle === "solid"
                  ? getAccentColor(buttonBorderColor)
                  : accentColor
              }
              trackingEvent={trackingEvent}
              trackingEventKey={trackingEventKey}
              trackingEventValue={trackingEventValue}
              className="shrink-0"
            />
          )}
        </div>
      </div>
      <div
        className={classNames(
          "relative flex items-end sm:items-center sm:justify-center",
          {
            "row-start-1 sm:col-start-2": reverseOrder,
          }
        )}
      >
        <div
          className={classNames(
            "flex aspect-square w-full items-end justify-center sm:aspect-auto",
            {
              "md:absolute md:left-0 md:top-1/2 md:-translate-y-1/2":
                size === "small",
            }
          )}
        >
          {videoUrl && (
            <Video
              video={videoUrl}
              playbackMode="autoplay"
              aspectRatio="1:1"
              coverImage={imageUrl ?? ""}
            />
          )}
          {!videoUrl && (imageUrl || mobileImageUrl) && (
            <GatsbyStoryblokImage
              image={(mobileImageUrl || imageUrl) as string}
              alt={imageAlt || "Banner image"}
              aspectRatio="1:1"
              imageObjectFit="contain"
              className={classNames(
                size === "large"
                  ? "max-h-[250px] sm:max-h-[430px]"
                  : "h-full w-full",
                { "my-16 sm:m-0": reverseOrder && size === "large" },
                { "mr-56": verticalLabel && size === "large" }
              )}
            />
          )}
          {verticalLabel && (
            <div className="absolute right-32 flex -translate-y-16 items-center sm:right-8 sm:-translate-y-1/2 md:right-16 lg:right-[30px]">
              <Typography
                font="grotesk"
                weight="book"
                size="subscript-lg"
                color={accentColor}
                text={verticalLabel}
                className="origin-center rotate-180 uppercase opacity-50 [writing-mode:vertical-lr]"
              />
            </div>
          )}
        </div>
      </div>

      {isSingleCTA && buttonLink && (
        <ClickableCardLink
          link={buttonLink}
          ariaLabel={header}
          trackingEvent={trackingEvent}
          trackingEventKey={trackingEventKey}
          trackingEventValue={trackingEventValue}
        />
      )}
    </LayoutItem>
  )
}

export default ContentBanner
